import React from 'react';
import { node } from 'prop-types';
import { SSRProvider } from 'react-aria';
import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import { noop } from 'nav-utilities';
import Footer from './components/Footer';
import { AppContext } from './context/appContext';
import { AnalyticsContextProvider } from './context/analyticsContext';
import { FOOTER_DIV_ID, PERFORMANCE_KEYS } from './constants';
import { NavOptionsProvider } from './context/optionsContext';
import { ErrorBoundary } from './components/AppErrorBoundary';
import { hydrateApp, renderApp, isReact18Plus } from './utils/reactCompat';

/**
 * An entry point for webpack and what will be the client bundle that is
 * created for the Footer application. Utilizes HMR when in development mode
 * and is also used for React's hydration.
 */

/**
 * Create the necessary providers which will be wrapped around the Footer
 * application.
 *
 * @param {*} props - React component props which will contain the children components to be wrapped.
 * @returns React component already wrapped with the necessary providers.
 */
// SSRProvider is not needed in React 18+ (improved SSR support built-in)
const SSRWrapper = ({ children }) => {
  if (isReact18Plus) {
    return <>{children}</>;
  }
  return <SSRProvider>{children}</SSRProvider>;
};

SSRWrapper.propTypes = {
  children: node,
};

// Create Emotion cache with same key as server-side to prevent hydration mismatch
const emotionCache = createCache({ key: 'nav-css' });

const AppProviders = props => {
  const { children } = props;

  return (
    <ErrorBoundary>
      <AppContext.Provider value={[window.NAV_INITIAL_DATA]}>
        <NavOptionsProvider>
          <CacheProvider value={emotionCache}>
            <SSRWrapper>
              <AnalyticsContextProvider>{children}</AnalyticsContextProvider>
            </SSRWrapper>
          </CacheProvider>
        </NavOptionsProvider>
      </AppContext.Provider>
    </ErrorBoundary>
  );
};

const stopHydrationTrace =
  window.NikeDotcomNav?.newrelic?.startTrace(PERFORMANCE_KEYS.FOOTER_HYDRATION) || noop;
// Used to hydrate the Footer application when on the client side.
// Uses hydrateApp for React 16/17/18 compatibility
hydrateApp(
  <AppProviders>
    <Footer />
  </AppProviders>,
  document.getElementById(FOOTER_DIV_ID),
  stopHydrationTrace,
);

// Used for Hot Module Reloading (HMR) when developing.
if (module.hot) {
  module.hot.accept('./components/Footer', async () => {
    const HMRApp = await import('./components/Footer');
    // Uses renderApp for React 16/17/18 compatibility
    renderApp(
      <AppProviders>
        <HMRApp />
      </AppProviders>,
      document.getElementById(FOOTER_DIV_ID),
    );
  });
}

AppProviders.propTypes = {
  children: node,
};
